import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';
import { getChatCompletion } from '../../../services/aiIntegrations/chatCompletion';
import toast from 'react-hot-toast';

const PLATFORM_COLORS = {
  facebook: 'text-blue-600 bg-blue-50',
  instagram: 'text-pink-600 bg-pink-50',
  google: 'text-orange-600 bg-orange-50',
  all: 'text-violet-600 bg-violet-50',
};

const STATUS_CONFIG = {
  active: { label: 'Active', className: 'bg-green-50 text-green-700', dot: 'bg-green-500' },
  paused: { label: 'Paused', className: 'bg-yellow-50 text-yellow-700', dot: 'bg-yellow-500' },
  draft: { label: 'Draft', className: 'bg-muted text-muted-foreground', dot: 'bg-muted-foreground' },
  completed: { label: 'Completed', className: 'bg-blue-50 text-blue-700', dot: 'bg-blue-500' },
  error: { label: 'Error', className: 'bg-red-50 text-red-700', dot: 'bg-red-500' },
};

const ActiveCampaignsTab = ({ campaigns, onRefresh, organization }) => {
  const [editingBudget, setEditingBudget] = useState(null);
  const [newBudget, setNewBudget] = useState('');
  const [loadingAction, setLoadingAction] = useState(null);
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [loadingSuggestion, setLoadingSuggestion] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  // Google Ads live campaigns state
  const [googleCampaigns, setGoogleCampaigns] = useState([]);
  const [loadingGoogle, setLoadingGoogle] = useState(false);
  const [googleError, setGoogleError] = useState(null);
  const [updatingGoogleStatus, setUpdatingGoogleStatus] = useState(null);

  const isGoogleConnected = !!organization?.google_ads_refresh_token && !!organization?.google_ads_customer_id;

  const fetchGoogleCampaigns = useCallback(async () => {
    if (!isGoogleConnected) return;
    setLoadingGoogle(true);
    setGoogleError(null);
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      const res = await fetch(
        `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/google-ads-campaigns`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({ action: 'list_campaigns' }),
        }
      );
      const data = await res?.json();
      if (data?.error) throw new Error(data.error);
      setGoogleCampaigns(data?.campaigns || []);
    } catch (err) {
      setGoogleError(err?.message || 'Failed to load Google Ads campaigns');
    } finally {
      setLoadingGoogle(false);
    }
  }, [isGoogleConnected]);

  useEffect(() => {
    fetchGoogleCampaigns();
  }, [fetchGoogleCampaigns]);

  const handleGoogleStatusToggle = async (campaign) => {
    setUpdatingGoogleStatus(campaign?.id);
    const newStatus = campaign?.status === 'ENABLED' ? 'PAUSED' : 'ENABLED';
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      const res = await fetch(
        `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/google-ads-campaigns`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({
            action: 'update_campaign_status',
            campaignResourceName: `customers/${organization?.google_ads_customer_id}/campaigns/${campaign?.id}`,
            newStatus,
          }),
        }
      );
      const data = await res?.json();
      if (data?.error) throw new Error(data.error);
      toast?.success(`Campaign ${newStatus === 'ENABLED' ? 'enabled' : 'paused'}`);
      fetchGoogleCampaigns();
    } catch (err) {
      toast?.error(err?.message || 'Failed to update campaign');
    } finally {
      setUpdatingGoogleStatus(null);
    }
  };

  const activeCampaigns = campaigns?.filter(c => c?.status === 'active' || c?.status === 'paused' || c?.status === 'draft');

  const handleTogglePause = async (campaign) => {
    setLoadingAction(campaign?.id);
    const newStatus = campaign?.status === 'active' ? 'paused' : 'active';
    try {
      // Update Meta API if connected
      if (campaign?.external_campaign_id && organization?.meta_access_token && (campaign?.platform === 'facebook' || campaign?.platform === 'instagram' || campaign?.platform === 'all')) {
        await fetch(`https://graph.facebook.com/v18.0/${campaign?.external_campaign_id}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            status: newStatus === 'active' ? 'ACTIVE' : 'PAUSED',
            access_token: organization?.meta_access_token,
          }),
        });
      }
      const { error } = await supabase?.from('ad_campaigns')?.update({ status: newStatus })?.eq('id', campaign?.id);
      if (error) throw error;
      toast?.success(`Campaign ${newStatus === 'active' ? 'resumed' : 'paused'}`);
      onRefresh?.();
    } catch (err) {
      toast?.error('Failed to update campaign status');
    } finally {
      setLoadingAction(null);
    }
  };

  const handleUpdateBudget = async (campaign) => {
    if (!newBudget || isNaN(parseFloat(newBudget))) { toast?.error('Enter a valid budget'); return; }
    setLoadingAction(campaign?.id + '_budget');
    try {
      const { error } = await supabase?.from('ad_campaigns')?.update({ daily_budget: parseFloat(newBudget) })?.eq('id', campaign?.id);
      if (error) throw error;
      toast?.success('Budget updated');
      setEditingBudget(null);
      setNewBudget('');
      onRefresh?.();
    } catch (err) {
      toast?.error('Failed to update budget');
    } finally {
      setLoadingAction(null);
    }
  };

  const handleDelete = async (campaign) => {
    setLoadingAction(campaign?.id + '_delete');
    try {
      const { error } = await supabase?.from('ad_campaigns')?.delete()?.eq('id', campaign?.id);
      if (error) throw error;
      toast?.success('Campaign deleted');
      setConfirmDelete(null);
      onRefresh?.();
    } catch (err) {
      toast?.error('Failed to delete campaign');
    } finally {
      setLoadingAction(null);
    }
  };

  const handleGetAISuggestion = async (campaign) => {
    setLoadingSuggestion(campaign?.id);
    try {
      const response = await getChatCompletion(
        'ANTHROPIC',
        'claude-sonnet-4-5-20250929',
        [{
          role: 'user',
          content: `Analyze this ad campaign and give 2-3 specific optimization suggestions in JSON:
Campaign: ${campaign?.name}
Platform: ${campaign?.platform}
Impressions: ${campaign?.impressions?.toLocaleString()}
Clicks: ${campaign?.clicks?.toLocaleString()}
CTR: ${campaign?.impressions > 0 ? ((campaign?.clicks / campaign?.impressions) * 100)?.toFixed(2) : 0}%
Spend: $${campaign?.spend}
Leads: ${campaign?.leads}
Daily Budget: $${campaign?.daily_budget}

Return JSON: {"suggestions": ["suggestion1", "suggestion2", "suggestion3"]}`
        }],
        { max_tokens: 400, temperature: 0.5 }
      );
      const content = response?.choices?.[0]?.message?.content || '';
      const jsonMatch = content?.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch?.[0]);
        setAiSuggestion({ id: campaign?.id, suggestions: parsed?.suggestions || [] });
      }
    } catch (err) {
      toast?.error('Failed to get AI suggestions');
    } finally {
      setLoadingSuggestion(null);
    }
  };

  if (activeCampaigns?.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="w-14 h-14 bg-violet-50 rounded-2xl flex items-center justify-center mb-4">
          <Icon name="Megaphone" size={26} className="text-violet-500" />
        </div>
        <h3 className="text-base font-semibold text-foreground mb-2">No active campaigns</h3>
        <p className="text-sm text-muted-foreground">Create and launch a campaign from the Create Campaign tab</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Google Ads Live Campaigns Section */}
      {isGoogleConnected && (
        <div className="bg-card border border-orange-200 rounded-xl overflow-hidden">
          <div className="flex items-center justify-between px-5 py-3 bg-orange-50/60 border-b border-orange-200">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 bg-orange-100 rounded-lg flex items-center justify-center">
                <Icon name="Search" size={14} className="text-orange-600" />
              </div>
              <div>
                <span className="text-sm font-semibold text-foreground">Google Ads — Live Campaigns</span>
                <span className="ml-2 text-xs text-muted-foreground">Customer ID: {organization?.google_ads_customer_id}</span>
              </div>
            </div>
            <button
              onClick={fetchGoogleCampaigns}
              disabled={loadingGoogle}
              className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-orange-700 border border-orange-200 bg-white rounded-lg hover:bg-orange-50 transition-colors disabled:opacity-50"
            >
              {loadingGoogle ? (
                <div className="w-3 h-3 border border-orange-300 border-t-orange-600 rounded-full animate-spin" />
              ) : (
                <Icon name="RefreshCw" size={12} />
              )}
              Refresh
            </button>
          </div>
          <div className="p-4">
            {loadingGoogle ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-6 h-6 border-2 border-orange-200 border-t-orange-600 rounded-full animate-spin" />
              </div>
            ) : googleError ? (
              <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                <Icon name="AlertCircle" size={15} className="text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs font-medium text-red-700">Failed to load campaigns</p>
                  <p className="text-xs text-red-600 mt-0.5">{googleError}</p>
                </div>
              </div>
            ) : googleCampaigns?.length === 0 ? (
              <div className="text-center py-6">
                <Icon name="Megaphone" size={22} className="text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">No campaigns found in your Google Ads account</p>
                <p className="text-xs text-muted-foreground mt-1">Create one from the Create Campaign tab</p>
              </div>
            ) : (
              <div className="space-y-2">
                {googleCampaigns?.map((campaign) => {
                  const isEnabled = campaign?.status === 'ENABLED';
                  const costUsd = (campaign?.costMicros / 1_000_000)?.toFixed(2);
                  return (
                    <div key={campaign?.id} className="flex items-center justify-between gap-3 p-3 bg-background border border-border rounded-lg">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${isEnabled ? 'bg-green-500 animate-pulse' : 'bg-yellow-400'}`} />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">{campaign?.name}</p>
                          <p className="text-xs text-muted-foreground">{campaign?.channelType} · ID: {campaign?.id}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 flex-shrink-0">
                        <div className="hidden sm:flex items-center gap-4 text-xs text-muted-foreground">
                          <span><span className="font-medium text-foreground">{Number(campaign?.impressions)?.toLocaleString()}</span> impr.</span>
                          <span><span className="font-medium text-foreground">{Number(campaign?.clicks)?.toLocaleString()}</span> clicks</span>
                          <span><span className="font-medium text-foreground">${costUsd}</span> spent</span>
                        </div>
                        <button
                          onClick={() => handleGoogleStatusToggle(campaign)}
                          disabled={updatingGoogleStatus === campaign?.id}
                          className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                            isEnabled
                              ? 'bg-yellow-50 text-yellow-700 border border-yellow-200 hover:bg-yellow-100' :'bg-green-50 text-green-700 border border-green-200 hover:bg-green-100'
                          }`}
                        >
                          {updatingGoogleStatus === campaign?.id ? (
                            <div className="w-3 h-3 border border-current/30 border-t-current rounded-full animate-spin" />
                          ) : (
                            <Icon name={isEnabled ? 'Pause' : 'Play'} size={11} />
                          )}
                          {isEnabled ? 'Pause' : 'Enable'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setConfirmDelete(null)} />
          <div className="relative bg-card border border-border rounded-2xl shadow-xl w-full max-w-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
                <Icon name="Trash2" size={18} className="text-red-600" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-foreground">Delete Campaign</h3>
                <p className="text-xs text-muted-foreground">This action cannot be undone</p>
              </div>
            </div>
            <p className="text-sm text-foreground mb-5">
              Are you sure you want to delete <strong>{confirmDelete?.name}</strong>?
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmDelete(null)}
                className="flex-1 py-2 border border-border rounded-lg text-sm font-medium hover:bg-muted transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(confirmDelete)}
                disabled={loadingAction === confirmDelete?.id + '_delete'}
                className="flex-1 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-60 transition-colors"
              >
                {loadingAction === confirmDelete?.id + '_delete' ? (
                  <div className="w-4 h-4 border border-white/30 border-t-white rounded-full animate-spin mx-auto" />
                ) : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {activeCampaigns?.map((campaign) => {
        const statusCfg = STATUS_CONFIG?.[campaign?.status] || STATUS_CONFIG?.draft;
        const platformColor = PLATFORM_COLORS?.[campaign?.platform] || 'text-muted-foreground bg-muted';
        const ctr = campaign?.impressions > 0 ? ((campaign?.clicks / campaign?.impressions) * 100)?.toFixed(2) : '0.00';
        const cpl = campaign?.leads > 0 ? (campaign?.spend / campaign?.leads)?.toFixed(2) : '—';
        const isThisSuggestion = aiSuggestion?.id === campaign?.id;

        return (
          <div key={campaign?.id} className="bg-card border border-border rounded-xl p-5">
            {/* Header */}
            <div className="flex items-start justify-between gap-3 mb-4">
              <div className="flex items-center gap-3 min-w-0">
                <div className={`px-2 py-1 rounded-lg text-xs font-medium ${platformColor}`}>
                  {campaign?.platform}
                </div>
                <div className="min-w-0">
                  <h3 className="font-semibold text-foreground text-sm truncate">{campaign?.name}</h3>
                  {campaign?.location && <p className="text-xs text-muted-foreground">{campaign?.location}</p>}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${statusCfg?.className}`}>
                  <div className={`w-1.5 h-1.5 rounded-full ${statusCfg?.dot} ${campaign?.status === 'active' ? 'animate-pulse' : ''}`} />
                  {statusCfg?.label}
                </div>
                <button
                  onClick={() => setConfirmDelete(campaign)}
                  className="p-1.5 rounded-lg text-muted-foreground hover:text-red-500 hover:bg-red-50 transition-colors"
                  title="Delete campaign"
                >
                  <Icon name="Trash2" size={13} />
                </button>
              </div>
            </div>
            {/* Metrics */}
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 mb-4">
              {[
                { label: 'Impressions', value: campaign?.impressions?.toLocaleString() || '0' },
                { label: 'Clicks', value: campaign?.clicks?.toLocaleString() || '0' },
                { label: 'CTR', value: `${ctr}%` },
                { label: 'Leads', value: campaign?.leads || '0' },
                { label: 'Spend', value: `$${campaign?.spend || '0'}` },
                { label: 'ROAS', value: campaign?.roas ? `${campaign?.roas}x` : '—' },
              ]?.map((metric, i) => (
                <div key={i} className="bg-background rounded-lg p-2.5 text-center">
                  <p className="text-xs text-muted-foreground mb-0.5">{metric?.label}</p>
                  <p className="text-sm font-bold text-foreground">{metric?.value}</p>
                </div>
              ))}
            </div>
            {/* Budget row */}
            <div className="flex items-center gap-2 mb-4 text-sm">
              <Icon name="DollarSign" size={14} className="text-muted-foreground" />
              <span className="text-muted-foreground">Daily budget:</span>
              {editingBudget === campaign?.id ? (
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={newBudget}
                    onChange={(e) => setNewBudget(e?.target?.value)}
                    className="w-24 px-2 py-1 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30 bg-background text-foreground"
                    placeholder={campaign?.daily_budget}
                    autoFocus
                  />
                  <button onClick={() => handleUpdateBudget(campaign)} disabled={loadingAction === campaign?.id + '_budget'}
                    className="px-2 py-1 bg-green-600 text-white rounded-lg text-xs font-medium hover:bg-green-700 transition-colors">
                    Save
                  </button>
                  <button onClick={() => { setEditingBudget(null); setNewBudget(''); }}
                    className="px-2 py-1 bg-muted text-muted-foreground rounded-lg text-xs font-medium hover:bg-muted/80 transition-colors">
                    Cancel
                  </button>
                </div>
              ) : (
                <>
                  <span className="font-semibold text-foreground">${campaign?.daily_budget}</span>
                  <button onClick={() => { setEditingBudget(campaign?.id); setNewBudget(campaign?.daily_budget); }}
                    className="text-xs text-violet-600 hover:text-violet-700 font-medium">Edit</button>
                </>
              )}
            </div>
            {/* AI Suggestions */}
            {isThisSuggestion && aiSuggestion?.suggestions?.length > 0 && (
              <div className="mb-4 p-3 bg-violet-50 border border-violet-200 rounded-xl">
                <p className="text-xs font-semibold text-violet-700 mb-2 flex items-center gap-1">
                  <Icon name="Sparkles" size={12} />
                  AI Optimization Suggestions
                </p>
                <ul className="space-y-1">
                  {aiSuggestion?.suggestions?.map((s, i) => (
                    <li key={i} className="text-xs text-violet-800 flex items-start gap-1.5">
                      <span className="text-violet-500 mt-0.5">•</span>
                      {s}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {/* Actions */}
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => handleTogglePause(campaign)}
                disabled={loadingAction === campaign?.id}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  campaign?.status === 'active' ?'bg-yellow-50 text-yellow-700 border border-yellow-200 hover:bg-yellow-100' :'bg-green-50 text-green-700 border border-green-200 hover:bg-green-100'
                }`}
              >
                {loadingAction === campaign?.id ? (
                  <div className="w-3 h-3 border border-current/30 border-t-current rounded-full animate-spin" />
                ) : (
                  <Icon name={campaign?.status === 'active' ? 'Pause' : 'Play'} size={12} />
                )}
                {campaign?.status === 'active' ? 'Pause' : 'Resume'}
              </button>
              <button
                onClick={() => handleGetAISuggestion(campaign)}
                disabled={loadingSuggestion === campaign?.id}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-violet-50 text-violet-700 border border-violet-200 hover:bg-violet-100 transition-colors"
              >
                {loadingSuggestion === campaign?.id ? (
                  <div className="w-3 h-3 border border-violet-300 border-t-violet-600 rounded-full animate-spin" />
                ) : (
                  <Icon name="Sparkles" size={12} />
                )}
                AI Suggestions
              </button>
              <span className="text-xs text-muted-foreground ml-auto">
                Created {new Date(campaign?.created_at)?.toLocaleDateString()}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ActiveCampaignsTab;
